import React from 'react';
import { User, Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ConversationMessageBubbleProps {
  content: string;
  isUser: boolean;
  isDarkMode: boolean;
}

const convertBareUrlsToMarkdownLinks = (text: string): string => {
  const markdownLinkOrImagePattern = /!?\[[^\]]*\]\([^)]+\)/g;
  const urlPattern = /(^|[\s(>])((https?:\/\/[^\s<)]+))/g;
  const segments: string[] = [];
  let lastIndex = 0;
  let markdownMatch: RegExpExecArray | null;

  while ((markdownMatch = markdownLinkOrImagePattern.exec(text)) !== null) {
    const start = markdownMatch.index;
    if (start > lastIndex) {
      segments.push(
        text.slice(lastIndex, start).replace(urlPattern, (_, prefix, url) => `${prefix}[${url}](${url})`)
      );
    }
    segments.push(markdownMatch[0]);
    lastIndex = start + markdownMatch[0].length;
  }

  if (lastIndex < text.length) {
    segments.push(
      text.slice(lastIndex).replace(urlPattern, (_, prefix, url) => `${prefix}[${url}](${url})`)
    );
  }

  return segments.join('');
};

const ConversationMessageBubble: React.FC<ConversationMessageBubbleProps> = ({ 
  content, 
  isUser, 
  isDarkMode 
}) => {
  const renderedContent = convertBareUrlsToMarkdownLinks(content);

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} px-2 sm:px-0`}>
      <div className={`flex gap-2 sm:gap-3 max-w-xs sm:max-w-2xl lg:max-w-3xl ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser 
            ? 'bg-blue-600' 
            : isDarkMode 
              ? 'bg-gray-700' 
              : 'bg-gray-200'
        }`}>
          {isUser ? (
            <User size={14} className="text-white sm:hidden" />
          ) : (
            <Bot size={14} className={`sm:hidden ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          )}
          {isUser ? (
            <User size={16} className="text-white hidden sm:block" />
          ) : (
            <Bot size={16} className={`hidden sm:block ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
          )}
        </div>
        
        {/* Message Content */}
        <div className={`rounded-xl sm:rounded-2xl p-3 sm:p-4 text-sm sm:text-base ${
          isUser
            ? 'bg-blue-600 text-white'
            : isDarkMode
              ? 'bg-gray-800 text-gray-100'
              : 'bg-gray-100 text-gray-900'
        } ${isUser ? 'rounded-br-md' : 'rounded-bl-md'}`}>
          <div className="prose prose-sm max-w-none break-words">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <div className="mb-2 last:mb-0">{children}</div>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
                a: ({ href, children }) => (
                  <a 
                    href={href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`underline hover:no-underline transition-colors ${
                      isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                    }`}
                  >
                    {children}
                  </a>
                ),
                ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
                li: ({ children }) => <li className="pl-0">{children}</li>,
                h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
                code: ({ children, className }) => {
                  // Inline code
                  if (!className) {
                    return (
                      <code className={`px-1 py-0.5 rounded text-xs font-mono ${
                        isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-800'
                      }`}>
                        {children}
                      </code>
                    );
                  }
                  // Code blocks
                  return (
                    <code className={`block p-3 rounded-md text-xs font-mono overflow-x-auto ${
                      isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {children}
                    </code>
                  );
                },
                blockquote: ({ children }) => (
                  <blockquote className={`border-l-4 pl-4 my-2 italic ${
                    isDarkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-600'
                  }`}>
                    {children}
                  </blockquote>
                )
              }}
            >
              {renderedContent}
            </ReactMarkdown>
          </div>
          {/* No timestamp shown */}
        </div>
      </div>
    </div>
  );
};

export default ConversationMessageBubble;
